<template>
  <div v-if="overcurrent">
    <n-alert
      :title="t('common.fitbike.error.motor.overcurrent.index')"
      :bordered="false"
    >
      <template #icon>
        <Icon
          icon="mdi:information-outline"
          :style="{ fontSize: '24px' }"
        />
      </template>
      <p>
        {{ t('common.fitbike.error.motor.overcurrent.content') }}
      </p>
    </n-alert>
  </div>
  <div v-if="stalled">
    <n-alert
      :title="t('common.fitbike.error.motor.stalled.index')"
      :bordered="false"
    >
      <template #icon>
        <Icon
          icon="mdi:information-outline"
          :style="{ fontSize: '24px' }"
        />
      </template>
      <p>
        {{ t('common.fitbike.error.motor.stalled.content') }}
      </p>
    </n-alert>
  </div>
  <div v-if="limitSwitch">
    <n-alert
      :title="t('common.fitbike.error.motor.limitSwitch.index')"
      :bordered="false"
    >
      <template #icon>
        <Icon
          icon="mdi:information-outline"
          :style="{ fontSize: '24px' }"
        />
      </template>
      <p>
        {{ t('common.fitbike.error.motor.limitSwitch.content') }}
      </p>
    </n-alert>
  </div>
  <div v-if="motorStatus.highTemperature || motorStatus.hasHadHighTemperature">
    <n-alert
      :title="t('common.fitbike.error.motor.highTemperature.index')"
      :bordered="false"
    >
      <template #icon>
        <Icon
          icon="mdi:information-outline"
          :style="{ fontSize: '24px' }"
        />
      </template>
      <p>
        {{ t('common.fitbike.error.motor.highTemperature.content') }}
      </p>
    </n-alert>
  </div>

  <div
    class="motor-error__actions"
    v-if="!motorStatus.highTemperature && !limitSwitch"
  >
    <BfButton
      v-if="connected"
      :type="ButtonType.Contained"
      @click="resetMotors"
    >
      {{ t('common.continue') }}
    </BfButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useDeviceStore } from '@/store/deviceStore'
import { usePositionStore } from '@/store/positionStore'
import { useMqttStore } from '@/store/mqttStore'
import { BfButton, ButtonType } from '@finetic/bikefitting-ui'
import { NAlert, useMessage } from 'naive-ui'
import { Icon } from '@iconify/vue'
import { deviceType } from '@/types/device'

const { t } = useI18n()
const deviceStore = useDeviceStore()
const positionStore = usePositionStore()
const mqttStore = useMqttStore()
const message = useMessage()

const { motorStatus } = storeToRefs(deviceStore)
const { connected } = storeToRefs(mqttStore)
const { doResetMotors } = positionStore

const props = withDefaults(
  defineProps<{
    device?: deviceType.handlebar | deviceType.saddle
  }>(),
  {
    device: deviceType.handlebar,
  },
)

const overcurrent = computed(() => {
  switch (props.device) {
    case deviceType.handlebar:
      return (
        motorStatus.value.handleBar.x.overcurrent ||
        motorStatus.value.handleBar.y.overcurrent
      )

    case deviceType.saddle:
      return (
        motorStatus.value.saddle.x.overcurrent ||
        motorStatus.value.saddle.y.overcurrent
      )

    default:
      return false
  }
})

const stalled = computed(() => {
  switch (props.device) {
    case deviceType.handlebar:
      return (
        motorStatus.value.handleBar.x.stalled ||
        motorStatus.value.handleBar.y.stalled
      )

    case deviceType.saddle:
      return (
        motorStatus.value.saddle.x.stalled || motorStatus.value.saddle.y.stalled
      )

    default:
      return false
  }
})

const limitSwitch = computed(() => {
  switch (props.device) {
    case deviceType.handlebar:
      return (
        motorStatus.value.handleBar.x.limitSwitch ||
        motorStatus.value.handleBar.y.limitSwitch
      )

    case deviceType.saddle:
      return (
        motorStatus.value.saddle.x.limitSwitch ||
        motorStatus.value.saddle.y.limitSwitch
      )

    default:
      return false
  }
})

const resetMotors = async () => {
  message.loading(t('common.fitbike.resetMotors'))

  doResetMotors()

  if (motorStatus.value.hasHadHighTemperature) {
    motorStatus.value.hasHadHighTemperature = false
  }
}
</script>

<style lang="scss" scoped>
.motor-error {
  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
  }
}
</style>
